// v10/src/components/AuditLog.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ChevronLeft, ChevronRight, RefreshCw, Plus, Pencil, Trash2, Eye, X, LogIn, Globe, User } from 'lucide-react';
import api from '../services/apiService';
import useIsMobile from '../hooks/useIsMobile';

// ── Helpers ───────────────────────────────────────────────────────
const ACTION_META = {
  CREATE: { label: 'Creó',          color: '#22c55e', bg: 'rgba(34,197,94,0.1)',   icon: Plus   },
  UPDATE: { label: 'Editó',         color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  icon: Pencil },
  DELETE: { label: 'Eliminó',       color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   icon: Trash2 },
  LOGIN:  { label: 'Inició sesión', color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', icon: LogIn  },
};

// ✅ Labels legibles para entidades
const ENTITY_LABELS = {
  inspection:            'Inspección',
  user:                  'Usuario',
  aircraft:              'Aeronave',
  squawk:                'Squawk',
  auth:                  'Autenticación',
  flight_authorization:  'Autorización de vuelo',
  organization:          'Organización',
  supplier:              'Proveedor',
  checklist:             'Checklist',
  hobbs:                 'Horómetro',
};

// ✅ Labels legibles para roles
const ROLE_LABELS_TEXT = {
  admin:      'Administrador',
  instructor: 'Instructor',
  pilot:      'Piloto',
  superadmin: 'Super Admin',
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day:'2-digit', month:'2-digit', year:'numeric' })
       + ' '
       + d.toLocaleTimeString('es-AR', { hour:'2-digit', minute:'2-digit' });
};

const ROLE_COLORS = {
  admin:      { bg:'rgba(139,92,246,0.12)', color:'#8b5cf6' },
  instructor: { bg:'rgba(59,130,246,0.12)', color:'#3b82f6' },
  pilot:      { bg:'rgba(34,197,94,0.12)',  color:'#22c55e' },
  superadmin: { bg:'rgba(239,68,68,0.12)',  color:'#ef4444' },
};

// ── Detail modal ──────────────────────────────────────────────────
const DetailModal = ({ entry, onClose, darkMode }) => {
  const isMobile = useIsMobile();
  if (!entry) return null;
  const meta  = ACTION_META[entry.action?.toUpperCase()] || ACTION_META.UPDATE;
  const color = darkMode ? 'rgba(255,255,255,0.7)' : '#374151';
  const label = darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8';

  const renderJson = (data) => {
    if (!data) return <span style={{ color: label, fontSize: '12px' }}>—</span>;
    const obj = typeof data === 'string' ? JSON.parse(data) : data;
    const keys = Object.keys(obj).filter(k => !['id', 'created_at', 'updated_at', 'organization_id', 'deleted_at'].includes(k));
    if (keys.length === 0) return <span style={{ color: label, fontSize: '12px' }}>Sin datos</span>;
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
        {keys.map(k => (
          <div key={k} style={{ display:'flex', gap:'8px', fontSize:'12px' }}>
            <span style={{ color: label, minWidth: isMobile ? '90px' : '130px', flexShrink:0 }}>{k.replace(/_/g, ' ')}</span>
            <span style={{ color, fontWeight:600, wordBreak:'break-word', flex: isMobile ? 1 : undefined, minWidth: isMobile ? 0 : undefined }}>{obj[k] === null ? '—' : typeof obj[k] === 'object' ? JSON.stringify(obj[k]) : String(obj[k])}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale:0.9, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.9, opacity:0 }}
        onClick={e => e.stopPropagation()}
        style={{ background: darkMode ? '#1e293b' : 'white', borderRadius:'24px', padding:'28px', maxWidth:'520px', width:'100%', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 24px 60px rgba(0,0,0,0.3)' }}
      >
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px' }}>
          <div style={{ width:'40px', height:'40px', borderRadius:'12px', background: meta.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <meta.icon style={{ width:'18px', height:'18px', color: meta.color }} />
          </div>
          <div style={{ flex:1 }}>
            <p style={{ margin:0, fontWeight:800, fontSize:'15px', color: darkMode?'white':'#0f172a' }}>
              {entry.description}
            </p>
            <p style={{ margin:0, fontSize:'12px', color: label }}>{formatDate(entry.created_at)}</p>
          </div>
          <button onClick={onClose} style={{ background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', border:'none', borderRadius:'10px', width:'32px', height:'32px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <X style={{ width:'15px', height:'15px', color: label }} />
          </button>
        </div>

        {/* ✅ Metadata con labels legibles */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'20px' }}>
          {[
            { l: 'Usuario',  v: entry.user_name },
            { l: 'Rol',      v: ROLE_LABELS_TEXT[entry.user_role] || entry.user_role },
            { l: 'Sección',  v: ENTITY_LABELS[entry.entity] || entry.entity },
            { l: 'Acción',   v: ACTION_META[entry.action?.toUpperCase()]?.label || entry.action },
            ...(entry.ip_address ? [{ l: 'Dirección IP', v: entry.ip_address }] : []),
            ...(entry.entity_id ? [{ l: 'ID entidad', v: `#${entry.entity_id}` }] : []),
          ].map(({ l, v }) => (
            <div key={l} style={{ background: darkMode?'rgba(255,255,255,0.04)':'#f8fafc', borderRadius:'12px', padding:'12px' }}>
              <p style={{ margin:'0 0 2px', fontSize:'11px', color: label, textTransform:'uppercase', letterSpacing:'0.06em' }}>{l}</p>
              <p style={{ margin:0, fontSize:'13px', fontWeight:700, color: darkMode?'white':'#0f172a' }}>{v}</p>
            </div>
          ))}
        </div>

        {/* Cambios antes/después */}
        {(entry.before_data || entry.after_data) && (
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : (entry.before_data && entry.after_data ? '1fr 1fr' : '1fr'), gap:'12px' }}>
            {entry.before_data && (
              <div style={{ background: darkMode?'rgba(239,68,68,0.06)':'rgba(239,68,68,0.04)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'14px', padding:'14px' }}>
                <p style={{ margin:'0 0 10px', fontSize:'11px', fontWeight:700, color:'#ef4444', textTransform:'uppercase', letterSpacing:'0.07em' }}>Antes</p>
                {renderJson(entry.before_data)}
              </div>
            )}
            {entry.after_data && (
              <div style={{ background: darkMode?'rgba(34,197,94,0.06)':'rgba(34,197,94,0.04)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:'14px', padding:'14px' }}>
                <p style={{ margin:'0 0 10px', fontSize:'11px', fontWeight:700, color:'#22c55e', textTransform:'uppercase', letterSpacing:'0.07em' }}>Después</p>
                {renderJson(entry.after_data)}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const AuditLog = ({ darkMode }) => {
  const isMobile = useIsMobile();
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [pages,   setPages]   = useState(1);
  const [detail,  setDetail]  = useState(null);

  const [filters, setFilters] = useState({ action: '', entity: '', date_from: '', date_to: '' });

  const LIMIT = 20;

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (filters.action)    params.append('action',    filters.action);
      if (filters.entity)    params.append('entity',    filters.entity);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to)   params.append('date_to',   filters.date_to);

      const res = await api.get(`/api/audit?${params}`);
      setLogs(res.data.data || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(p);
    } catch (err) {
      setError('No se pudieron cargar los logs de auditoría.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  const text   = darkMode ? 'rgba(255,255,255,0.85)' : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.4)'  : '#64748b';
  const card   = darkMode ? 'rgba(255,255,255,0.04)' : 'white';
  const border = darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0';

  return (
    <div style={{ padding:'0 0 40px' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap: isMobile ? '10px' : '12px', marginBottom:'22px' }}>
        <div style={{ width: isMobile ? '38px' : '44px', height: isMobile ? '38px' : '44px', borderRadius:'14px', background:'linear-gradient(135deg,#6d28d9,#1d4ed8)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <ShieldCheck style={{ width: isMobile ? '19px' : '22px', height: isMobile ? '19px' : '22px', color:'white' }} />
        </div>
        <div style={{ minWidth:0 }}>
          <h2 style={{ margin:0, fontSize: isMobile ? '17px' : '20px', fontWeight:900, color: text, whiteSpace: isMobile ? 'nowrap' : undefined }}>Logs de Auditoría</h2>
          <p style={{ margin:0, fontSize: isMobile ? '12px' : '13px', color: sub }}>{total} registro{total !== 1 ? 's' : ''} en total</p>
        </div>
        <motion.button onClick={() => fetchLogs(page)} whileTap={{ scale:0.93 }}
          aria-label="Actualizar"
          style={{ marginLeft:'auto', padding: isMobile ? '9px' : '10px', background: darkMode?'rgba(255,255,255,0.06)':'#f1f5f9', border:'none', borderRadius:'12px', cursor:'pointer', color: sub, display:'flex', alignItems:'center', gap:'6px', fontSize:'13px', fontWeight:600, flexShrink:0 }}>
          <RefreshCw style={{ width:'15px', height:'15px' }} /> {!isMobile && 'Actualizar'}
        </motion.button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', gap:'10px', marginBottom:'18px', flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
        <select
          value={filters.action}
          onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
          style={{ padding:'9px 14px', borderRadius:'12px', border:`1px solid ${border}`, background: card, color: text, fontSize:'13px', fontWeight:600, cursor:'pointer', width: isMobile ? '100%' : undefined, boxSizing:'border-box' }}
        >
          <option value=''>Todas las acciones</option>
          <option value='CREATE'>Creó</option>
          <option value='UPDATE'>Editó</option>
          <option value='DELETE'>Eliminó</option>
          <option value='LOGIN'>Inició sesión</option>
        </select>

        <select
          value={filters.entity}
          onChange={e => setFilters(f => ({ ...f, entity: e.target.value }))}
          style={{ padding:'9px 14px', borderRadius:'12px', border:`1px solid ${border}`, background: card, color: text, fontSize:'13px', fontWeight:600, cursor:'pointer', width: isMobile ? '100%' : undefined, boxSizing:'border-box' }}
        >
          <option value=''>Todas las secciones</option>
          {Object.entries(ENTITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        {isMobile ? (
          <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
            <label style={{ fontSize:'11px', fontWeight:700, color: sub, textTransform:'uppercase', letterSpacing:'0.06em' }}>Desde</label>
            <input type='date' value={filters.date_from}
              onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
              style={{ padding:'9px 14px', borderRadius:'12px', border:`1px solid ${border}`, background: card, color: text, fontSize:'13px', width:'100%', boxSizing:'border-box' }}
            />
          </div>
        ) : (
          <input type='date' value={filters.date_from}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
            style={{ padding:'9px 14px', borderRadius:'12px', border:`1px solid ${border}`, background: card, color: text, fontSize:'13px' }}
          />
        )}

        {isMobile ? (
          <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
            <label style={{ fontSize:'11px', fontWeight:700, color: sub, textTransform:'uppercase', letterSpacing:'0.06em' }}>Hasta</label>
            <input type='date' value={filters.date_to}
              onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
              style={{ padding:'9px 14px', borderRadius:'12px', border:`1px solid ${border}`, background: card, color: text, fontSize:'13px', width:'100%', boxSizing:'border-box' }}
            />
          </div>
        ) : (
          <input type='date' value={filters.date_to}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
            style={{ padding:'9px 14px', borderRadius:'12px', border:`1px solid ${border}`, background: card, color: text, fontSize:'13px' }}
          />
        )}

        {(filters.action || filters.entity || filters.date_from || filters.date_to) && (
          <motion.button onClick={() => setFilters({ action:'', entity:'', date_from:'', date_to:'' })} whileTap={{ scale:0.93 }}
            style={{ padding:'9px 14px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'12px', color:'#ef4444', fontSize:'13px', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent: isMobile ? 'center' : undefined, gap:'6px', width: isMobile ? '100%' : undefined, boxSizing:'border-box' }}>
            <X style={{ width:'13px', height:'13px' }} /> Limpiar
          </motion.button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 0', color: sub }}>
          <motion.div animate={{ rotate:360 }} transition={{ duration:1, repeat:Infinity, ease:'linear' }}
            style={{ width:'32px', height:'32px', border:`3px solid ${border}`, borderTopColor:'#6d28d9', borderRadius:'50%', margin:'0 auto 12px' }} />
          Cargando logs...
        </div>
      ) : error ? (
        <div style={{ textAlign:'center', padding:'40px', color:'#ef4444', background:'rgba(239,68,68,0.06)', borderRadius:'16px', border:'1px solid rgba(239,68,68,0.15)' }}>
          {error}
        </div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px', color: sub }}>
          <ShieldCheck style={{ width:'40px', height:'40px', margin:'0 auto 12px', display:'block', opacity:0.3 }} />
          No hay registros para los filtros seleccionados
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {logs.map((entry, i) => {
            const meta = ACTION_META[entry.action?.toUpperCase()] || ACTION_META.UPDATE;
            const role = ROLE_COLORS[entry.user_role] || ROLE_COLORS.pilot;
            const Icon = meta.icon;
            return (
              <motion.div key={entry.id}
                initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.03 }}
                style={{ background: card, border:`1px solid ${border}`, borderRadius:'16px', padding:'14px 18px', display:'flex', alignItems:'center', gap:'14px', cursor:'pointer' }}
                onClick={() => setDetail(entry)}
                whileHover={{ scale:1.005, boxShadow: darkMode?'0 4px 20px rgba(0,0,0,0.3)':'0 4px 20px rgba(0,0,0,0.06)' }}
              >
                <div style={{ width:'36px', height:'36px', borderRadius:'10px', background: meta.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Icon style={{ width:'16px', height:'16px', color: meta.color }} />
                </div>

                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ margin:'0 0 3px', fontSize:'13px', fontWeight:700, color: text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {entry.description}
                  </p>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'11px', fontWeight:700, padding:'2px 8px', borderRadius:'6px', background: role.bg, color: role.color }}>
                      {ROLE_LABELS_TEXT[entry.user_role] || entry.user_role}
                    </span>
                    <span style={{ display:'flex', alignItems:'center', gap:'3px', fontSize:'12px', color: sub }}>
                      <User style={{ width:'11px', height:'11px' }} /> {entry.user_name}
                    </span>
                    {entry.entity && (
                      <span style={{ fontSize:'11px', fontWeight:600, padding:'2px 7px', borderRadius:'6px', background: darkMode?'rgba(255,255,255,0.06)':'#f1f5f9', color: sub }}>
                        {ENTITY_LABELS[entry.entity] || entry.entity}
                      </span>
                    )}
                    {entry.ip_address && (
                      <span style={{ display:'flex', alignItems:'center', gap:'3px', fontSize:'11px', color: darkMode?'rgba(255,255,255,0.25)':'#94a3b8' }}>
                        <Globe style={{ width:'10px', height:'10px' }} /> {entry.ip_address}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <p style={{ margin:'0 0 4px', fontSize:'12px', color: sub }}>{formatDate(entry.created_at)}</p>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:'4px', fontSize:'11px', color: darkMode?'rgba(139,92,246,0.7)':'#6d28d9', fontWeight:600 }}>
                    <Eye style={{ width:'11px', height:'11px' }} /> Ver detalle
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'12px', marginTop:'24px' }}>
          <motion.button onClick={() => fetchLogs(page - 1)} disabled={page <= 1} whileTap={{ scale:0.93 }}
            style={{ padding:'8px 14px', background: card, border:`1px solid ${border}`, borderRadius:'10px', cursor: page<=1?'not-allowed':'pointer', opacity: page<=1?0.4:1, color: text, display:'flex', alignItems:'center', gap:'4px', fontSize:'13px', fontWeight:600 }}>
            <ChevronLeft style={{ width:'15px', height:'15px' }} /> Anterior
          </motion.button>
          <span style={{ fontSize:'13px', color: sub }}>{page} / {pages}</span>
          <motion.button onClick={() => fetchLogs(page + 1)} disabled={page >= pages} whileTap={{ scale:0.93 }}
            style={{ padding:'8px 14px', background: card, border:`1px solid ${border}`, borderRadius:'10px', cursor: page>=pages?'not-allowed':'pointer', opacity: page>=pages?0.4:1, color: text, display:'flex', alignItems:'center', gap:'4px', fontSize:'13px', fontWeight:600 }}>
            Siguiente <ChevronRight style={{ width:'15px', height:'15px' }} />
          </motion.button>
        </div>
      )}

      <AnimatePresence>
        {detail && <DetailModal entry={detail} onClose={() => setDetail(null)} darkMode={darkMode} />}
      </AnimatePresence>
    </div>
  );
};

export default AuditLog;
